import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { FileBox } from "lucide-react";

const schema = z.object({
  displayName: z.string().trim().min(2, "Nome muito curto").max(100),
  email: z.string().trim().email("Email inválido").max(255),
  password: z
    .string()
    .min(8, "Mínimo 8 caracteres")
    .max(72)
    .regex(/[A-Z]/, "Inclua uma letra maiúscula")
    .regex(/[a-z]/, "Inclua uma letra minúscula")
    .regex(/[0-9]/, "Inclua um número")
    .regex(/[^A-Za-z0-9]/, "Inclua um caractere especial"),
  invite: z
    .string()
    .trim()
    .min(1, "Informe o código de convite")
    .transform((v) => v.toUpperCase()),
});

export default function SignupPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ displayName: "", email: "", password: "", invite: "" });
  const [loading, setLoading] = useState(false);

  // Força Light Mode na Landing Page
  useEffect(() => {
    const root = document.documentElement;
    const hadDark = root.classList.contains("dark");
    if (hadDark) {
      root.classList.remove("dark");
      root.classList.add("light");
    }
    return () => {
      if (hadDark) {
        root.classList.remove("light");
        root.classList.add("dark");
      }
    };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      toast({ title: "Verifique os campos", description: parsed.error.issues[0].message, variant: "destructive" });
      return;
    }
    setLoading(true);

    // Pré-valida o convite (feedback rápido; o trigger valida e consome no servidor)
    const { data: ok, error: vErr } = await supabase.rpc("validate_invite", { _code: parsed.data.invite });
    if (vErr || !ok) {
      setLoading(false);
      toast({ title: "Convite inválido", description: "Código não encontrado, expirado ou esgotado.", variant: "destructive" });
      return;
    }

    const { error } = await supabase.auth.signUp({
      email: parsed.data.email,
      password: parsed.data.password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`,
        data: { display_name: parsed.data.displayName, invite_code: parsed.data.invite },
      },
    });
    setLoading(false);
    if (error) {
      const msg = /invite/i.test(error.message)
        ? "Código de convite inválido ou esgotado."
        : error.message;
      toast({ title: "Erro no cadastro", description: msg, variant: "destructive" });
      return;
    }
    toast({ title: "Conta criada", description: "Verifique seu email se a confirmação estiver ativa, ou faça login." });
    navigate("/login");
  }

  return (
    <div className="min-h-screen grid place-items-center bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-50/50 via-white to-blue-50/50 text-slate-900 font-sans p-4 relative overflow-hidden">
      
      {/* Animated Liquid Background (Optimized) */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0 opacity-40">
        <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[70%] bg-blue-300 liquid-blob" />
        <div className="absolute top-[10%] right-[-10%] w-[50%] h-[80%] bg-sky-200 liquid-blob" style={{ animationDelay: "-5s", animationDuration: "25s, 12s" }} />
        <div className="absolute bottom-[-20%] left-[20%] w-[70%] h-[60%] bg-indigo-200 liquid-blob" style={{ animationDelay: "-10s", animationDuration: "22s, 10s" }} />
      </div>

      <div className="relative w-full max-w-md group z-10">
        <div className="absolute -inset-1 bg-gradient-to-r from-[#33CCFF] to-[#1A66CC] rounded-[2rem] blur opacity-20 group-hover:opacity-30 transition duration-1000 group-hover:duration-200" />
        <div className="bg-white/90 dark:bg-white/5 backdrop-blur-2xl rounded-3xl p-6 lg:p-8 shadow-2xl shadow-blue-900/10 dark:shadow-[0_8px_32px_rgba(0,0,0,0.4)] border border-white/50 dark:border-white/10 relative z-10">
          
          <div className="flex items-center gap-4 mb-8">
            <div className="size-12 rounded-xl grid place-items-center bg-[linear-gradient(135deg,#33CCFF,#1A66CC)] shadow-[0_4px_16px_rgba(51,153,255,0.45)]">
              <FileBox className="size-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-slate-900 dark:text-white">Criar conta</h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Cadastre-se no Extrator Fiscal</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Nome</Label>
              <Input id="name" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} required 
                className="h-12 bg-white dark:bg-black/20 border-slate-300 dark:border-white/10 focus:border-[#33CCFF] dark:focus:border-[#33CCFF] focus:ring-[#33CCFF] pl-4 transition-all" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Email</Label>
              <Input id="email" type="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required 
                className="h-12 bg-white dark:bg-black/20 border-slate-300 dark:border-white/10 focus:border-[#33CCFF] dark:focus:border-[#33CCFF] focus:ring-[#33CCFF] pl-4 transition-all" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Senha</Label>
              <Input id="password" type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required 
                className="h-12 bg-white dark:bg-black/20 border-slate-300 dark:border-white/10 focus:border-[#33CCFF] dark:focus:border-[#33CCFF] focus:ring-[#33CCFF] pl-4 transition-all" />
              <p className="text-[11px] text-slate-500 dark:text-slate-400">Mín. 8 caracteres, com maiúscula, minúscula, número e símbolo.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite" className="text-sm font-semibold text-slate-700 dark:text-slate-300">Convite</Label>
              <Input
                id="invite"
                value={form.invite}
                onChange={(e) => setForm({ ...form, invite: e.target.value.toUpperCase() })}
                placeholder="Digite seu código"
                maxLength={16}
                autoComplete="off"
                required
                className="h-12 bg-white dark:bg-black/20 border-slate-300 dark:border-white/10 focus:border-[#33CCFF] dark:focus:border-[#33CCFF] focus:ring-[#33CCFF] pl-4 uppercase transition-all"
              />
              <p className="text-[11px] text-slate-500 dark:text-slate-400">Cadastro restrito. Informe o código fornecido pelo administrador.</p>
            </div>
            <Button type="submit" className="w-full h-12 mt-2 text-base font-semibold bg-[linear-gradient(135deg,#33CCFF,#1A66CC)] hover:opacity-90 border-0 text-white shadow-lg shadow-blue-500/25" disabled={loading}>
              {loading ? "Criando..." : "Criar conta"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Já tem conta? <Link to="/login" className="text-blue-600 dark:text-[#33CCFF] font-semibold hover:underline">Entrar</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
